import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useInView } from "react-intersection-observer";
import { Link, useHistory } from "react-router-dom";
import Select from "react-select";
import toast from "react-hot-toast";

import { createClientSchema } from "../../schema";
import {
  Input,
  Button,
  Map,
  UploadField,
  ErrorMessage,
  Card,
  MainContent,
  Grid,
  Buttons,
} from "../../components";
import {
  fetchCountryInfo,
  fetchCountries,
  fetchClientCategories,
  createClient,
} from "../../redux/actions";
import {
  getCountryOptions,
  getCityOptions,
  getCategoryOptions,
} from "../../utils";
import { dismissToast, somethignWentWrong } from "../../helpers";

export const CreateClient = () => {
  const clientCategories = useSelector(({ clients }) => clients.categories);
  const countryManagementState = useSelector(
    ({ countryManagement }) => countryManagement
  );
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const history = useHistory();
  const dispatch = useDispatch();
  const { ref, inView } = useInView();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(createClientSchema),
  });

  const handleChange = useCallback(
    (selectedOption) => {
      dispatch(fetchCountryInfo(selectedOption.id));
      setValue("cityName", "");
    },
    [setValue, dispatch]
  );

  const handleCreateClient = useCallback(
    (data) => {
      dismissToast();
      dispatch(createClient(data, address, lat, lng))
        .then((res) => {
          if (res.status === 201) {
            setTimeout(() => history.push("/client-management"), [2000]);
            toast.success(`${data.name} has been created Successfully`);
          } else if (res.status === 409) {
            toast.error("User already exist");
          } else if (res.status === 401) {
            history.push("/");
          } else {
            somethignWentWrong();
          }
        })
        .catch((e) => {
          somethignWentWrong();
        });
    },
    [dispatch, address, lat, lng, history]
  );

  useEffect(() => {
    dispatch(fetchCountries());
    dispatch(fetchClientCategories());
  }, [dispatch]);

  return (
    <MainContent>
      <form onSubmit={handleSubmit(handleCreateClient)} noValidate>
        <div className="container_mobile">
          <div className="page-header" ref={ref}>
            <h1>Client Management</h1>
            <div className={inView ? `` : `sticky-action-bar`}>
              <Buttons noMargin>
                <Link to="/client-management" className="btn btn-secondary">
                  Back
                </Link>
                <Button type="submit" variant="primary">
                  Create
                </Button>
              </Buttons>
            </div>
          </div>
        </div>
        <div className="cards_wrapper">
          <Card title="Create Client">
            <Grid columns={2}>
              <Input
                label="Client Name"
                type="text"
                placeholder="Client Name"
                name="name"
                register={register}
                errors={errors}
              />
              <div className="input_holder">
                <label>Client Category</label>
                <Controller
                  name="category"
                  errors={errors}
                  control={control}
                  render={({ field }) => (
                    <Select
                      menuPortalTarget={document.body}
                      {...field}
                      options={getCategoryOptions(clientCategories)}
                      placeholder="Select Category"
                      className={`kmmrce-select-container ${
                        errors && errors["category"] && `error`
                      }`}
                      classNamePrefix="kmmrce-select"
                    />
                  )}
                />
                {errors && errors["category"] && (
                  <ErrorMessage text="Client Category is Required" />
                )}
              </div>
            </Grid>
            <Grid columns={2}>
              <Input
                label="Email Address"
                type="email"
                placeholder="Email Address"
                name="email"
                register={register}
                errors={errors}
              />
              <Input
                label="Client Contact Number"
                type="tel"
                placeholder="Client Contact Number"
                name="mobileNumber"
                register={register}
                errors={errors}
              />
            </Grid>
            <UploadField
              label="Client Logo"
              name="logo"
              errors={errors}
              register={register}
            />
          </Card>
          <Card title="Location">
            <Grid columns={3}>
              <div className="input_holder">
                <label>Country Name</label>
                <Controller
                  name="countryName"
                  errors={errors}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      menuPortalTarget={document.body}
                      options={getCountryOptions(
                        countryManagementState.countries
                      )}
                      placeholder="Select Country"
                      className={`kmmrce-select-container ${
                        errors && errors["countryName"] && `error`
                      }`}
                      classNamePrefix="kmmrce-select"
                      onChange={(e) => {
                        onChange(e);
                        handleChange(e);
                      }}
                      value={value ? value : ""}
                    />
                  )}
                />
                {errors && errors["countryName"] && (
                  <ErrorMessage text="Country is Required" />
                )}
              </div>
              <div className="input_holder">
                <label>City Name</label>
                <Controller
                  name="cityName"
                  control={control}
                  render={({ field }) => (
                    <Select
                      menuPortalTarget={document.body}
                      {...field}
                      options={getCityOptions(
                        countryManagementState?.countryInfo?.cities
                      )}
                      placeholder="Select City"
                      isLoading={countryManagementState?.countryInfoLoading}
                      loadingMessage={() => "Loading..."}
                      className={`kmmrce-select-container ${
                        errors && errors["cityName"] && `error`
                      }`}
                      classNamePrefix="kmmrce-select"
                    />
                  )}
                />
                {errors && errors["cityName"] && (
                  <ErrorMessage text="City is Required" />
                )}
              </div>
              <Input
                label="Client P.O. Box"
                type="text"
                placeholder="Client P.O. Box"
                name="poBox"
                register={register}
                errors={errors}
              />
            </Grid>
            <Map
              addressInfo={(address, lat, lng) => {
                setAddress(address);
                setLat(lat);
                setLng(lng);
              }}
            />
          </Card>
        </div>
      </form>
    </MainContent>
  );
};

export default CreateClient;
